import api from '@/lib/api';
import type {
  HotelReviewDto,
  VideoStoryDto,
  HotelReviewInput,
  VideoStoryInput,
  PresignUploadRequest,
  PresignedUploadResponse,
} from '@pms/shared';

/**
 * Admin data layer for curated storefront stories (TASK-62): hotel reviews +
 * video stories CRUD and the presigned-upload endpoint. Thin axios fns over
 * `@/lib/api` (paths under `/admin/platform/*`), mirroring `platform.api.ts`.
 */

export type {
  HotelReviewDto,
  VideoStoryDto,
  HotelReviewInput,
  VideoStoryInput,
  PresignUploadRequest,
  PresignedUploadResponse,
} from '@pms/shared';

// ─── Hotel reviews ─────────────────────────────────────────────────────────────

export async function fetchHotelReviews(): Promise<HotelReviewDto[]> {
  const { data } = await api.get<HotelReviewDto[]>('/admin/platform/hotel-reviews');
  return data;
}

export async function createHotelReview(
  body: HotelReviewInput,
): Promise<HotelReviewDto> {
  const { data } = await api.post<HotelReviewDto>('/admin/platform/hotel-reviews', body);
  return data;
}

export async function updateHotelReview(
  id: string,
  body: Partial<HotelReviewInput>,
): Promise<HotelReviewDto> {
  const { data } = await api.patch<HotelReviewDto>(
    `/admin/platform/hotel-reviews/${id}`,
    body,
  );
  return data;
}

export async function deleteHotelReview(id: string): Promise<void> {
  await api.delete(`/admin/platform/hotel-reviews/${id}`);
}

// ─── Video stories ─────────────────────────────────────────────────────────────

export async function fetchVideoStories(): Promise<VideoStoryDto[]> {
  const { data } = await api.get<VideoStoryDto[]>('/admin/platform/video-stories');
  return data;
}

export async function createVideoStory(
  body: VideoStoryInput,
): Promise<VideoStoryDto> {
  const { data } = await api.post<VideoStoryDto>('/admin/platform/video-stories', body);
  return data;
}

export async function updateVideoStory(
  id: string,
  body: Partial<VideoStoryInput>,
): Promise<VideoStoryDto> {
  const { data } = await api.patch<VideoStoryDto>(
    `/admin/platform/video-stories/${id}`,
    body,
  );
  return data;
}

export async function deleteVideoStory(id: string): Promise<void> {
  await api.delete(`/admin/platform/video-stories/${id}`);
}

// ─── Presigned upload ──────────────────────────────────────────────────────────

/**
 * POST /admin/platform/storefront/uploads/presign — returns a presigned PUT URL
 * (+ the public URL to store). Throws a 503 axios error when storage is
 * unconfigured; the caller degrades to a manual paste-URL field.
 */
export async function presignStorefrontUpload(
  body: PresignUploadRequest,
): Promise<PresignedUploadResponse> {
  const { data } = await api.post<PresignedUploadResponse>(
    '/admin/platform/storefront/uploads/presign',
    body,
  );
  return data;
}
